<script setup>
import { useLayout2HomeFiltersStore } from "@/modules/Layout2/stores/useLayout2HomeFilters.store";
import { useLayout2NewReleasesStore } from "@/modules/Layout2/stores/useLayout2NewReleases.store";
import { useLayout2OverdueAssignmentsStore } from "@/modules/Layout2/stores/useLayout2OverdueAssignments.store";
import { ChevronUpIcon } from "@heroicons/vue/solid";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import HomeNewRelease from "../assigments/HomeNewRelease.vue";
import HomeOverdueAssignments from "../assigments/HomeOverdueAssignments.vue";
import HomeThisWeekAssignments from "../assigments/HomeThisWeekAssignments.vue";
import HomeLastWeekAssignments from "../assigments/HomeLastWeekAssignments.vue";
import { useLayout2WeeklyAssignedContentsStore } from "@/modules/Layout2/stores/useLayout2WeeklyAssignedContents.store";
import Layout2Filters from "@/modules/Layout2/components/partials/Layout2Filters.vue";
import { watch } from "vue";

const layout2FiltersStore = useLayout2HomeFiltersStore();
const layout2OverdueAssignments = useLayout2OverdueAssignmentsStore();
const newReleasesStore = useLayout2NewReleasesStore();
const layout2WeeklyAssignedContentStore =
  useLayout2WeeklyAssignedContentsStore();

const { selectedFilter, selectedTimeline } = storeToRefs(layout2FiltersStore);
const { overdueAssignments } = storeToRefs(layout2OverdueAssignments);
const { newReleases } = storeToRefs(newReleasesStore);
const { currentWeekAssigned, lastWeekAssigned } = storeToRefs(
  layout2WeeklyAssignedContentStore
);

const selectedTab = ref("");
const loadingAssignments = ref(false);

const tabs = [
  { name: "New Releases", tab: "new_releases", component: HomeNewRelease },
  {
    name: "This Week's Assignments",
    tab: "current_week",
    component: HomeThisWeekAssignments,
  },
  {
    name: "Last Week's Assignments",
    tab: "last_week",
    component: HomeLastWeekAssignments,
  },
  {
    name: "Overdue Assignments",
    tab: "overdue",
    component: HomeOverdueAssignments,
  },
];

const filteredTabs = computed(() => {
  return tabs.filter((tab) => {
    if (selectedTimeline.value && selectedTimeline.value !== tab.tab)
      return false;
    return true;
  });
});

/**
 * Fetch Overdue Assignments
 * @returns void;
 */
const fetch_overdue_assignments = async () => {
  await layout2OverdueAssignments.handleFetchOverdueAssignmentsContents();
  layout2FiltersStore.handleFilterBySort(selectedFilter.value.sort === "a-z");
  layout2FiltersStore.handleFilterByProgress(selectedFilter.value.filter);
  layout2FiltersStore.handleFilterByContentType();
};

/**
 * Fetch New Releases
 * @returns void;
 */
const fetch_new_release = async () => {
  await newReleasesStore.handleFetchNewReleases();
  layout2FiltersStore.handleFilterBySort(selectedFilter.value.sort === "a-z");
  layout2FiltersStore.handleFilterByProgress(selectedFilter.value.filter);
  layout2FiltersStore.handleFilterByContentType();
};

/**
 * Fetch This Week Assignments
 * @returns void;
 */
const fetch_this_week_assignments = async () => {
  await layout2WeeklyAssignedContentStore.handleFetchCurrentWeekAssignedContents();
  layout2FiltersStore.handleFilterBySort(selectedFilter.value.sort === "a-z");
  layout2FiltersStore.handleFilterByProgress(selectedFilter.value.filter);
  layout2FiltersStore.handleFilterByContentType();
};

const fetch_last_week_assignments = async () => {
  await layout2WeeklyAssignedContentStore.handleFetchLastWeekAssignedContents();
  layout2FiltersStore.handleFilterBySort(selectedFilter.value.sort === "a-z");
  layout2FiltersStore.handleFilterByProgress(selectedFilter.value.filter);
  layout2FiltersStore.handleFilterByContentType();
};

/**
 * Click tab to open/close
 * @param tab - tab to click
 * @returns void;
 */
const click_tab = (tab) => {
  if (selectedTab.value === tab) {
    selectedTab.value = "";
    return;
  }
  selectedTab.value = tab;
};

/**
 * Initialize the open tab based on the assignments available
 * Check if not empty
 * Open the first tab with available assignments
 */
const initialize_open_tab = () => {
  if (newReleases.value.length) {
    selectedTab.value = "new_releases";
    return;
  }
  if (currentWeekAssigned.value.length) {
    selectedTab.value = "current_week";
    return;
  }
  if (lastWeekAssigned.value.length) {
    selectedTab.value = "last_week";
    return;
  }
  if (overdueAssignments.value.length) {
    selectedTab.value = "overdue";
    return;
  }
};

/**
 * Fetch all assignments
 * @returns void;
 */
const fetch_assignments = () => {
  loadingAssignments.value = true;
  Promise.all([
    fetch_new_release(),
    fetch_overdue_assignments(),
    fetch_this_week_assignments(),
    fetch_last_week_assignments(),
  ])
    .then(() => {
      initialize_open_tab();
    })
    .finally(() => {
      loadingAssignments.value = false;
    });
};

watch(filteredTabs, (value) => {
  if (value.length === 1) {
    selectedTab.value = value[0].tab;
    return;
  }
  selectedTab.value = "";
  initialize_open_tab();
});

onMounted(() => {
  fetch_assignments();
});
</script>
<template>
  <div class="w-full py-3 flex flex-col gap-6">
    <Layout2Filters />
    <div class="w-full flex flex-col gap-2" v-if="!loadingAssignments">
      <div
        class="w-full flex justify-center items-center rounded-md overflow-hidden flex-col transition-all duration-1000"
        v-for="tab in filteredTabs"
        :key="tab.name"
      >
        <div
          class="w-full flex flex-row px-4 py-6"
          :class="{
            'text-white bg-brand': selectedTab === tab.tab,
            'text-white bg-brand-light hover:bg-brand hover:text-white':
              selectedTab !== tab.tab,
          }"
          @click="click_tab(tab.tab)"
        >
          <div
            class="flex justify-start items-center flex-1 text-lg font-normal"
          >
            {{ tab.name }}
          </div>
          <div class="flex justify-center items-center">
            <ChevronUpIcon
              class="size-6 transition-all duration-300"
              :class="{
                'rotate-180': selectedTab === tab.tab,
              }"
            />
          </div>
        </div>
        <div
          class="py-4 px-4 flex flex-col w-full bg-untitled-gray-200 transition-all duration-700"
          v-if="selectedTab && selectedTab === tab.tab"
        >
          <component :is="tab.component" />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-2 w-full" v-if="loadingAssignments">
      <div
        class="rounded-md bg-brand-light animate-pulse py-10 px-10"
        v-for="n in 4"
        :key="n"
      ></div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
